import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material';
import { clsx } from 'clsx';
import * as R from 'ramda';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { SelectAutocomplete } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { TaskField } from '@atom/types/task';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './taskFields.module.css';

interface Props {
  field: TaskField;
  taskId?: string;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  fieldsLoading?: Set<string>;
  previewView?: boolean;
  isNested?: boolean;
  isTemplate?: boolean;
  error?: boolean;
  helperText?: string;
  onClick?: () => void;
  invalidFieldIds?: Set<string>;
  prevPath?: string[];
  setCascadeActivePaths?: Dispatch<
    SetStateAction<{
      [key: string]: string[];
    }>
  >;
  customFieldView: CustomFieldView;
}

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

const NESTED_FIELD_WIDTH = '9rem';

const TaskEnumSingleField = ({
  field,
  error,
  onChange,
  isDisabled,
  isTemplate,
  fieldsLoading = new Set([]),
  previewView,
  isNested,
  invalidFieldIds = new Set([]),
  helperText,
  onClick,
  setCascadeActivePaths,
  prevPath = [],
  customFieldView,
}: Props) => {
  const [value, setValue] = useState<string>(field.value);

  const handleChange = (event: any, option: string) => {
    const newValue = R.isNil(option) ? '' : option;

    setValue(newValue);
    onChange(field.id, newValue);
  };

  useEffect(() => {
    // Preselect value if there is exactly one option
    if (
      !isTemplate &&
      !isDisabled &&
      field?.enumeration?.length === 1 &&
      !field.value
    ) {
      handleChange({}, field.enumeration[0]);
    }
  }, [field.enumeration, isDisabled, isTemplate]);

  useEffect(() => {
    setValue(field.value);
  }, [field?.value]);

  const nestedField = useMemo(() => {
    if (!field.subFields) {
      return null;
    }

    const nestedFieldIndex = R.findIndex(R.propEq(value, 'matchValue'))(
      field.subFields,
    );

    return field.subFields[nestedFieldIndex]?.nestedField;
  }, [value, field]);

  useEffect(() => {
    if (isNilOrEmpty(nestedField) && !!setCascadeActivePaths) {
      setCascadeActivePaths(prev => ({
        ...prev,
        [prevPath[0]]: [...prevPath, field.id],
      }));
    }
  }, [nestedField]);

  const title =
    field.required && customFieldView !== CustomFieldView.FILTER
      ? `* ${field.title}`
      : field.title;
  const inputError = error || invalidFieldIds.has(field.id);

  const loading = field.subFieldsTruncated && fieldsLoading.has(field.id);
  const hasSubFields = !R.isEmpty(field.subFields);
  const containerWidth =
    hasSubFields || isNested
      ? {
          marginRight: '1.5rem',
          marginBottom: '1rem',
          maxWidth: NESTED_FIELD_WIDTH,
        }
      : {
          maxWidth: getTaskFieldWidth(customFieldView),
        };

  const displayText = value ? `${title}: ${value}` : title;

  return (
    <>
      <div
        className={classes.fieldContainer}
        onClick={onClick}
        style={containerWidth}
      >
        {hasSubFields || isNested ? (
          <TextOverflowTooltip
            width={NESTED_FIELD_WIDTH}
            text={displayText}
            color={inputError ? colors.utility.muiError : colors.neutral.dim}
            fontSize={fonts.sm}
          />
        ) : (
          <div
            className={clsx(
              classes.fieldTitle,
              error && classes.fieldTitleError,
            )}
          >
            {displayText}
          </div>
        )}
        <SelectAutocomplete
          id={field.id}
          options={field.enumeration}
          value={value}
          onChange={handleChange}
          disabled={isDisabled}
          loading={loading}
          helperText={helperText}
          filterOptions={filterOptions}
          TextFieldProps={{ error: inputError, onClick }}
          error={inputError}
        />
      </div>
      {nestedField && !loading && (
        <TaskEnumSingleField
          field={{ ...nestedField, required: field.required }}
          onChange={onChange}
          isDisabled={isDisabled}
          isTemplate={isTemplate}
          fieldsLoading={fieldsLoading}
          previewView={previewView}
          isNested
          invalidFieldIds={invalidFieldIds}
          helperText={helperText}
          onClick={onClick}
          prevPath={[...prevPath, field.id]}
          setCascadeActivePaths={setCascadeActivePaths}
          customFieldView={customFieldView}
        />
      )}
    </>
  );
};

export default TaskEnumSingleField;
